import { useState } from 'react';
import {
  Gavel,
  FileText,
  Clock,
  Users,
  Plus,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import { useSourcingEvents } from '@/features/sourcing/hooks';
import {
  SourcingEventCard,
  AddEventModal,
  EventDetailModal,
  type AddEventFormData,
} from '@/features/sourcing/components';
import {
  type SourcingEvent, type SourcingEventType 
} from '@/features/sourcing/types';
import { toast } from 'sonner';

interface EventsViewProps {
  readonly filterType?: SourcingEventType;
  readonly title: string;
  readonly description: string;
}

export function EventsView({
  filterType, title, description 
}: EventsViewProps) {
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<SourcingEvent | null>(null);
  const [formData, setFormData] = useState<AddEventFormData>({
    title: '',
    eventType: filterType ?? 'sealed_proposal',
    deliveryZone: '',
    requiredDeliveryDate: '',
    bidDeadline: '',
    estimatedValue: '',
    description: '',
  });

  const {
    data, isLoading, refetch 
  } = useSourcingEvents();
  const events = data?.data ?? [];
  const filteredEvents = filterType
    ? events.filter((e) => e.eventType === filterType)
    : events;

  const liveAuctions = filteredEvents.filter((e) => {
    if (e.eventType !== 'reverse_auction') return false;
    const now = Date.now();
    const start = e.auctionStartTime ? new Date(e.auctionStartTime).getTime() : 0;
    const end = e.auctionEndTime ? new Date(e.auctionEndTime).getTime() : 0;
    return now >= start && now < end;
  });

  const scheduledEvents = filteredEvents.filter((e) => e.status === 'scheduled');
  const openEvents = filteredEvents.filter((e) => e.status === 'open' && !liveAuctions.includes(e));
  const evaluationEvents = filteredEvents.filter((e) => e.status === 'evaluation');

  const handleAddEvent = () => {
    setFormData({
      title: '',
      eventType: filterType ?? 'sealed_proposal',
      deliveryZone: '',
      requiredDeliveryDate: '',
      bidDeadline: '',
      estimatedValue: '',
      description: '',
    });
    setShowAddModal(true);
  };

  const handleSaveEvent = () => {
    if (!formData.title || !formData.deliveryZone || !formData.bidDeadline) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success(`Created sourcing event: ${formData.title}`);
    setShowAddModal(false);
    refetch();
  };

  const handleDeleteEvents = () => {
    toast.success(`Deleted ${selectedEvents.length} event(s)`);
    setSelectedEvents([]);
  };

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Imported sourcing events');
      refetch();
    }, 1500);
  };

  const handleViewDetails = (event: SourcingEvent) => {
    setSelectedEvent(event);
  };

  const exportData = filteredEvents.map((e) => ({
    eventNumber: e.eventNumber,
    title: e.title,
    eventType: e.eventType,
    status: e.status,
    deliveryZone: e.deliveryZone,
    estimatedValue: e.estimatedValue,
    bidDeadline: e.bidDeadline,
    totalBids: e.totalBids ?? 0,
  }));

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="h-64 animate-pulse bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-muted-foreground">{description}</p>
        </div>
        <Button onClick={handleAddEvent}>
          <Plus className="h-4 w-4 mr-2" />
          Create Event
        </Button>
      </div>

      <CrudToolbar
        entityName="Sourcing Event"
        entityNamePlural="Sourcing Events"
        selectedCount={selectedEvents.length}
        onAdd={handleAddEvent}
        onDelete={handleDeleteEvents}
        onBulkUpload={handleBulkUpload}
        exportData={exportData}
        exportFilename="sourcing-events.csv"
        canEdit={false}
      />

      <AddEventModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveEvent}
        formData={formData}
        setFormData={setFormData}
      />

      <EventDetailModal
        event={selectedEvent}
        isOpen={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
      />

      <StatsSection
        liveCount={liveAuctions.length}
        openCount={openEvents.length}
        scheduledCount={scheduledEvents.length}
        evaluationCount={evaluationEvents.length}
        showAuctions={!filterType || filterType === 'reverse_auction'}
      />

      {liveAuctions.length > 0 && (
        <Section title="Live Auctions" icon={<Gavel className="h-5 w-5 text-orange-600" />} isLive>
          {liveAuctions.map((event) => (
            <SourcingEventCard key={event.eventId} event={event} onViewDetails={handleViewDetails} />
          ))}
        </Section>
      )}

      {scheduledEvents.length > 0 && (
        <Section title="Scheduled" icon={<Clock className="h-5 w-5" />}>
          {scheduledEvents.map((event) => (
            <SourcingEventCard key={event.eventId} event={event} onViewDetails={handleViewDetails} />
          ))}
        </Section>
      )}

      {openEvents.length > 0 && (
        <Section title="Open for Proposals" icon={<FileText className="h-5 w-5" />}>
          {openEvents.map((event) => (
            <SourcingEventCard key={event.eventId} event={event} onViewDetails={handleViewDetails} />
          ))}
        </Section>
      )}

      {evaluationEvents.length > 0 && (
        <Section title="Under Evaluation" icon={<Users className="h-5 w-5" />}>
          {evaluationEvents.map((event) => (
            <SourcingEventCard key={event.eventId} event={event} onViewDetails={handleViewDetails} />
          ))}
        </Section>
      )}

      {filteredEvents.length === 0 && <EmptyState onAdd={handleAddEvent} filterType={filterType} />}
    </div>
  );
}

interface StatCardProps {
  readonly title: string;
  readonly value: number;
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly variant?: 'default' | 'warning';
}

function StatCard({
  title, value, icon: iconProp, variant = 'default' 
}: StatCardProps) {
  const StatIcon = iconProp;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <StatIcon className={`h-5 w-5 ${variant === 'warning' ? 'text-orange-500' : 'text-muted-foreground'}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

interface StatsSectionProps {
  readonly liveCount: number;
  readonly openCount: number;
  readonly scheduledCount: number;
  readonly evaluationCount: number;
  readonly showAuctions?: boolean;
}

function StatsSection({
  liveCount,
  openCount,
  scheduledCount,
  evaluationCount,
  showAuctions = true,
}: StatsSectionProps) {
  return (
    <div className={`grid gap-4 ${showAuctions ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
      {showAuctions && <StatCard title="Live Auctions" value={liveCount} icon={Gavel} variant="warning" />}
      <StatCard title="Open" value={openCount} icon={FileText} />
      <StatCard title="Scheduled" value={scheduledCount} icon={Clock} />
      <StatCard title="Under Evaluation" value={evaluationCount} icon={Users} />
    </div>
  );
}

interface SectionProps {
  readonly title: string;
  readonly icon: React.ReactNode;
  readonly isLive?: boolean;
  readonly children: React.ReactNode;
}

function Section({
  title, icon, isLive, children 
}: SectionProps) {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
        {icon}
        {title}
        {isLive && (
          <span className="relative flex h-3 w-3 ml-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500" />
          </span>
        )}
      </h3>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{children}</div>
    </div>
  );
}

interface EmptyStateProps {
  readonly onAdd: () => void;
  readonly filterType?: SourcingEventType;
}

function EmptyState({
  onAdd, filterType 
}: EmptyStateProps) {
  const messageMap: Record<string, string> = {
    reverse_auction: 'No reverse auctions found',
    sealed_proposal: 'No sealed proposals found',
  };
  const message = (filterType && messageMap[filterType]) ?? 'No sourcing events';

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12">
        <Gavel className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg font-medium">{message}</p>
        <p className="text-muted-foreground mb-4">Create a new event to start sourcing</p>
        <Button onClick={onAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Create Event
        </Button>
      </CardContent>
    </Card>
  );
}
